import { useState, useEffect } from "react";
import { supabase } from "../integrations/supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";
import { Camera, X, Folder, Calendar, ExternalLink, ChevronLeft, ChevronRight, Share2, Facebook, Twitter, Link2, MessageCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import ConferenceMoments from "@/components/ConferenceMoments";
import PageLoader from "@/components/PageLoader";
import { useImagePreloader } from "@/hooks/useImagePreloader";
import { OptimizedImage } from "@/components/ui/OptimizedImage";

interface GalleryPhoto {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  event_name: string;
  event_date: string | null;
  created_at: string;
}

interface GalleryAlbum {
  id: string;
  event_name: string;
  event_date: string | null;
  description: string | null;
  external_link: string | null;
  cover_image_url: string | null;
  created_at: string;
}

const Gallery = () => {
  const [albums, setAlbums] = useState<GalleryAlbum[]>([]);
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [filteredPhotos, setFilteredPhotos] = useState<GalleryPhoto[]>([]);
  const [filteredAlbums, setFilteredAlbums] = useState<GalleryAlbum[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<GalleryPhoto | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentAlbum, setCurrentAlbum] = useState<GalleryAlbum | null>(null);
  const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
  const [selectedAlbumForView, setSelectedAlbumForView] = useState<GalleryAlbum | null>(null);
  const [albumPhotosForView, setAlbumPhotosForView] = useState<GalleryPhoto[]>([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const [eventNameFilter, setEventNameFilter] = useState<string>("");
  const [eventDateFilter, setEventDateFilter] = useState<string>("");
  const [galleryLink, setGalleryLink] = useState<string>("https://sites.google.com/view/ofuesi-2024/pictures?authuser=0");
  const [galleryLabel, setGalleryLabel] = useState<string>("OFUESI-2024 Pictures");

  useEffect(() => {
    (async () => {
      const { data } = await (supabase.from("app_settings" as any) as any)
        .select("value").eq("key", "gallery_link").maybeSingle();
      if (data?.value?.url) setGalleryLink(data.value.url);
      if (data?.value?.label) setGalleryLabel(data.value.label);
    })();
  }, []);

  const trackClick = async (source: string) => {
    try {
      await (supabase.from("gallery_click_events" as any) as any).insert({
        link: galleryLink, source, user_agent: navigator.userAgent.slice(0, 255),
      });
    } catch (e) { /* non-blocking */ }
  };

  const shareUrl = encodeURIComponent(galleryLink);
  const shareText = encodeURIComponent(`Check out the OFUESI 2024 photo gallery: ${galleryLabel}`);
  const shareTargets = [
    { name: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${shareText}%20${shareUrl}`, color: "bg-[#25D366] hover:bg-[#1ebe5a]" },
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, color: "bg-[#1877F2] hover:bg-[#0e63d0]" },
    { name: "X", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`, color: "bg-black hover:bg-gray-800" },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(galleryLink);
      toast({ title: "Link copied", description: "Gallery link copied to clipboard." });
      trackClick("copy_link");
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };




  useEffect(() => {
    document.title = "Gallery - OFUESI";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "View photos from OFUESI conferences, events, and fellowship gatherings across the United States.",
      );
    }
    fetchAlbums();
    fetchPhotos();
  }, []);

  const fetchAlbums = async () => {
    try {
      const { data, error } = await supabase
        .from("gallery_albums")
        .select("*")
        .eq("is_public", true)
        .order("event_date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAlbums(data || []);
    } catch (error) {
      console.error("Error fetching albums:", error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const { data, error } = await supabase
        .from("gallery_photos")
        .select("*")
        .eq("is_public", true)
        .order("event_date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPhotos(data || []);
      setFilteredPhotos(data || []);
    } catch (error) {
      console.error("Error fetching photos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = photos;

    // Apply event name filter
    if (eventNameFilter) {
      filtered = filtered.filter((photo) => photo.event_name.toLowerCase().includes(eventNameFilter.toLowerCase()));
    }

    // Apply event date filter
    if (eventDateFilter && eventDateFilter !== "all-dates") {
      filtered = filtered.filter((photo) => photo.event_date === eventDateFilter);
    }

    setFilteredPhotos(filtered);
  }, [photos, eventNameFilter, eventDateFilter]);

  useEffect(() => {
    let filtered = albums;

    // Apply event name filter
    if (eventNameFilter) {
      filtered = filtered.filter((album) => album.event_name.toLowerCase().includes(eventNameFilter.toLowerCase()));
    }

    // Apply event date filter
    if (eventDateFilter && eventDateFilter !== "all-dates") {
      filtered = filtered.filter((album) => album.event_date === eventDateFilter);
    }

    setFilteredAlbums(filtered);
  }, [albums, eventNameFilter, eventDateFilter]);

  const handleAlbumFolderClick = (album: GalleryAlbum) => {
    setSelectedAlbumForView(album);
    const albumPhotos = photos.filter((photo) => photo.event_name === album.event_name);
    setAlbumPhotosForView(albumPhotos);
    setAlbumDialogOpen(true);
  };

  // Preload only visible images for better performance
  const imageUrls = [
    ...filteredAlbums
      .slice(0, 12)
      .map((a) => a.cover_image_url)
      .filter(Boolean),
    ...filteredPhotos.slice(0, 4).map((p) => p.image_url),
  ].filter(Boolean) as string[];

  const { imagesLoaded, preloadedImages } = useImagePreloader({ imageUrls, enabled: !loading });

  if (loading || !imagesLoaded) {
    return <PageLoader message="Loading gallery..." showNavigation={true} />;
  }

  return (
    <div className="min-h-screen faith-pattern" style={{ background: "var(--background)" }}>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan py-4 relative">
        <div className="absolute inset-0 faith-pattern opacity-20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto text-white space-y-6">
            <div className="flex justify-center mb-4">
              <Camera className="h-16 w-16 text-white" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white drop-shadow-lg">Photo Gallery</h1>
            <p className="text-xl leading-relaxed text-white">
              Capturing moments of faith, fellowship, and ministry across America
            </p>
            <div className="pt-2 space-y-4">
              <a
                href={galleryLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick("hero_button")}
                className="inline-flex items-center gap-2 bg-white text-primary hover:bg-amber-50 font-semibold px-6 py-3 rounded-full shadow-lg transition-colors"
                aria-label={`View ${galleryLabel} (opens in new tab)`}
              >
                <Camera className="h-5 w-5" />
                View {galleryLabel}
              </a>

              {/* Share buttons */}
              <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                <span className="inline-flex items-center gap-1 text-sm text-white/90"><Share2 className="h-4 w-4" /> Share:</span>
                {shareTargets.map((s) => (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackClick(`share_${s.name.toLowerCase()}`)}
                    aria-label={`Share on ${s.name}`}
                    className={`${s.color} text-white p-2 rounded-full shadow transition-colors`}
                  >
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
                <button
                  type="button"
                  onClick={copyLink}
                  aria-label="Copy gallery link"
                  className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full shadow transition-colors"
                >
                  <Link2 className="h-4 w-4" />
                </button>
              </div>

              {/* Album thumbnail preview cards */}
              {albums.length > 0 && (
                <div className="pt-4">
                  <p className="text-sm text-white/90 mb-3">Preview the album:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
                    {albums
                      .filter((a) => a.cover_image_url)
                      .slice(0, 4)
                      .map((a) => (
                        <a
                          key={a.id}
                          href={galleryLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackClick("thumbnail")}
                          className="group relative block rounded-lg overflow-hidden shadow-lg ring-2 ring-white/30 hover:ring-white transition-all"
                          aria-label={`Open ${a.event_name} in gallery`}
                        >
                          <OptimizedImage
                            src={a.cover_image_url!}
                            alt={a.event_name}
                            className="w-full h-24 sm:h-28 object-cover"
                            aspectRatio="4/3"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-end p-2">
                            <span className="text-xs text-white font-medium line-clamp-1">{a.event_name}</span>
                          </div>
                        </a>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Event Photo Gallery Section */}
      <section className="py-4">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-foreground mb-8">Event Photo Gallery</h2>

          {/* Filters */}
          <div className="mb-8 flex flex-wrap gap-4 justify-center">
            <div className="w-full max-w-xs">
              <label className="text-sm font-medium mb-2 block">Filter by Event Name</label>
              <Input
                placeholder="Search event name..."
                value={eventNameFilter}
                onChange={(e) => setEventNameFilter(e.target.value)}
                className="bg-background"
              />
            </div>
            <div className="w-full max-w-xs">
              <label className="text-sm font-medium mb-2 block">Filter by Event Date</label>
              <Select value={eventDateFilter} onValueChange={setEventDateFilter}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select date" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="all-dates">All Dates</SelectItem>
                  {Array.from(new Set(albums.map((a) => a.event_date).filter(Boolean)))
                    .sort()
                    .reverse()
                    .map((date) => (
                      <SelectItem key={date} value={date!}>
                        {new Date(date!).toLocaleDateString()}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {(eventNameFilter || eventDateFilter) && (
              <div className="w-full max-w-xs flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEventNameFilter("");
                    setEventDateFilter("");
                  }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>

          {/* Album Folders */}
          {albums.length > 0 && (
            <div className="mb-12">
              <h3 className="text-xl font-semibold mb-6 text-center">Browse by Album</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                {filteredAlbums.map((album) => (
                  <motion.div
                    key={album.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col items-center cursor-pointer group"
                    onClick={() => handleAlbumFolderClick(album)}
                  >
                    <Folder
                      className="h-24 w-24 text-primary group-hover:text-primary/80 transition-colors"
                      fill="currentColor"
                      strokeWidth={1.5}
                    />
                    <p className="text-center text-sm font-medium mt-2 line-clamp-2 px-2">{album.event_name}</p>
                    {album.event_date && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(album.event_date).toLocaleDateString()}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Album Details Dialog */}
      <Dialog open={albumDialogOpen} onOpenChange={setAlbumDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5 text-primary" fill="currentColor" strokeWidth={1.5} />
              {selectedAlbumForView?.event_name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {selectedAlbumForView?.event_date && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {new Date(selectedAlbumForView.event_date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
            )}

            {selectedAlbumForView?.description && (
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm">{selectedAlbumForView.description}</p>
              </div>
            )}

            {selectedAlbumForView?.external_link && (
              <div>
                <Button variant="outline" onClick={() => window.open(selectedAlbumForView.external_link!, "_blank")}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Full Gallery
                </Button>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold mb-4">Photos</h3>
              {albumPhotosForView.length === 0 ? (
                <div className="text-center py-12 border rounded-lg bg-muted/20">
                  <p className="text-muted-foreground">No photos in this album yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {albumPhotosForView.map((photo) => (
                    <Card
                      key={photo.id}
                      className="cursor-pointer overflow-hidden"
                      onClick={() => {
                        const photoIndex = albumPhotosForView.findIndex(p => p.id === photo.id);
                        setSelectedPhotoIndex(photoIndex);
                        setSelectedPhoto(photo);
                        setAlbumDialogOpen(false);
                      }}
                    >
                      <CardContent className="p-0">
                        <OptimizedImage
                          src={photo.image_url}
                          alt={photo.title}
                          className="w-full h-48"
                          aspectRatio="4/3"
                          loading="lazy"
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox Dialog with Carousel */}
      <Dialog 
        open={!!selectedPhoto} 
        onOpenChange={() => {
          setSelectedPhoto(null);
          setSelectedPhotoIndex(0);
        }}
      >
        <DialogContent className="max-w-6xl p-0 overflow-hidden">
          {selectedPhoto && albumPhotosForView.length > 0 && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-10 bg-gray-600/80 hover:bg-gray-700 text-white rounded-full backdrop-blur-sm shadow-lg transition-all duration-300"
                onClick={() => {
                  setSelectedPhoto(null);
                  setSelectedPhotoIndex(0);
                }}
              >
                <X className="h-6 w-6" />
              </Button>

              {/* Previous Button */}
              {albumPhotosForView.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-gray-600/80 hover:bg-gray-700 hover:text-white text-white rounded-full backdrop-blur-sm shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedPhotoIndex === 0}
                  onClick={() => {
                    const newIndex = selectedPhotoIndex - 1;
                    setSelectedPhotoIndex(newIndex);
                    setSelectedPhoto(albumPhotosForView[newIndex]);
                  }}
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
              )}

              {/* Next Button */}
              {albumPhotosForView.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-gray-600/80 hover:bg-gray-700 hover:text-white text-white rounded-full backdrop-blur-sm shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedPhotoIndex === albumPhotosForView.length - 1}
                  onClick={() => {
                    const newIndex = selectedPhotoIndex + 1;
                    setSelectedPhotoIndex(newIndex);
                    setSelectedPhoto(albumPhotosForView[newIndex]);
                  }}
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              )}

              <OptimizedImage
                key={albumPhotosForView[selectedPhotoIndex].id}
                src={albumPhotosForView[selectedPhotoIndex].image_url}
                alt={albumPhotosForView[selectedPhotoIndex].title}
                className="w-full max-h-[85vh]"
                loading="eager"
                showSkeleton={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
